import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, ArrowUpRight } from 'lucide-react';
import { Reveal } from './motion';
import DarkSection from './DarkSection';

const EXPLORE_LINKS = [
  { name: 'About Us', to: '/about' },
  { name: 'Services', to: '/services' },
  { name: 'Available Candidates', to: '/candidates' },
  { name: 'Job Board', to: '/jobs' },
  { name: 'Testimonials', to: '/testimonials' },
  { name: 'Contact', to: '/contact' },
];

const LEGAL_LINKS = [
  { name: 'Privacy Policy', to: '/privacy-policy' },
  { name: 'Cookie Policy', to: '/cookie-policy' },
  { name: 'Terms of Service', to: '/terms-of-service' },
  { name: 'Modern Slavery Statement', to: '/modern-slavery-statement' },
  { name: 'Sitemap', to: '/sitemap' },
];

const PublicFooter = () => (
  <DarkSection as="footer" glow="top-center" style={{ borderTop: '1px solid var(--border-dark)' }}>
    <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-16 md:px-8 md:pt-20">
      <Reveal>
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <img src="/appsytec-logo.png" alt="Appsytec" className="h-12 w-auto object-contain" />
            <p className="mt-5 max-w-sm text-gray-400">
              A boutique Australian SME connecting public sector organisations with high-calibre ICT professionals:
              small, agile, and accountable.
            </p>
            <div className="mt-6 space-y-3 text-sm text-gray-400">
              <a href="mailto:contact@appsytec.com" className="flex items-center gap-3 transition-colors hover:text-red-400">
                <Mail className="h-4 w-4 text-red-400" /> contact@appsytec.com
              </a>
              <span className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-red-400" /> Melbourne, Australia
              </span>
            </div>
          </div>

          {/* Explore */}
          <div className="md:col-span-3">
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-gray-500">
              <span className="text-red-400">[</span> Explore <span className="text-red-400">]</span>
            </h3>
            <ul className="mt-5 space-y-3">
              {EXPLORE_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="group inline-flex items-center gap-1 text-gray-300 transition-colors hover:text-red-400">
                    {link.name}
                    <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-all duration-200 group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-4">
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-gray-500">
              <span className="text-red-400">[</span> Legal <span className="text-red-400">]</span>
            </h3>
            <ul className="mt-5 space-y-3">
              {LEGAL_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-gray-300 transition-colors hover:text-red-400">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>

      <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row">
        <p className="text-sm text-gray-500">© {new Date().getFullYear()} Appsytec Pty Ltd. All rights reserved.</p>
        <div className="flex items-center gap-5">
          <a
            href="https://www.linkedin.com/company/appsytec"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 transition-colors hover:text-red-400"
            aria-label="Follow us on LinkedIn"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
          <p className="text-xs text-gray-600">Designed and developed in-house by Appsytec.</p>
        </div>
      </div>
    </div>
  </DarkSection>
);

export default PublicFooter;
